import { Component, input, signal } from '@angular/core';
import { PdfViewerModule } from 'ng2-pdf-viewer';

const ZOOM_INICIAL = 0.7;
const ZOOM_MINIMO = 0.5;
const ZOOM_MAXIMO = 3;
const PASSO_ZOOM = 0.2;

/**
 * Visualizador de PDF com zoom e tela cheia, usado no extrato de desligamento e na assinatura do termo.
 */
@Component({
  selector: 'app-pdf-document',
  imports: [PdfViewerModule],
  styleUrl: './pdf-document.scss',
  templateUrl: './pdf-document.html',
})
export class PdfDocument {
  /** Endereço do PDF; enquanto vazio, o visualizador não é renderizado. */
  readonly src = input<string | undefined>();
  /**
   * Texto exibido com o ícone de check à esquerda dos controles (ex.: "Visualizado em 04/09/2026 às 15:15").
   * Vazio: o bloco fica vazio, mantendo os controles à direita.
   */
  readonly textoInformativo = input('');
  /** 'sucesso': texto verde com o ícone de check; 'aviso': texto amarelo, sem ícone. */
  readonly tipoInformativo = input<'sucesso' | 'aviso'>('sucesso');
  /** Texto opcional exibido em preto ao lado do texto informativo. */
  readonly textoAdicional = input('');

  protected readonly zoomMinimo = ZOOM_MINIMO;
  protected readonly zoomMaximo = ZOOM_MAXIMO;
  readonly zoom = signal(ZOOM_INICIAL);
  readonly isFullscreen = signal(false);
  private alturaAntesDoFullscreen: number | null = null;

  zoomIn(): void {
    if (this.zoom() < ZOOM_MAXIMO) {
      this.zoom.update((zoom) => zoom + PASSO_ZOOM);
    }
  }

  zoomOut(): void {
    if (this.zoom() > ZOOM_MINIMO) {
      this.zoom.update((zoom) => zoom - PASSO_ZOOM);
    }
  }

  toggleFullscreen(): void {
    if (!this.isFullscreen()) {
      /**
       * Guarda a altura atual (real, com o layout normal) ANTES de entrar em tela cheia. Ao sair,
       * restauramos esse valor em vez de remedir o documento: enquanto o iframe pai ainda não
       * encolheu de volta, qualquer `height: 100%`/`vh` na árvore do documento continua resolvendo
       * contra o viewport (ainda grande) do próprio iframe, então uma remedição nesse momento
       * relata um valor tão grande quanto — ou maior que — o da tela cheia, num loop de
       * realimentação que nunca deixa o iframe encolher.
       */
      this.alturaAntesDoFullscreen = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    }

    this.isFullscreen.update((ativo) => !ativo);
    this.recalcularTamanhoDoPdf();
  }

  /**
   * O ng2-pdf-viewer (zoom-scale="page-width") só recalcula o tamanho das páginas ao ouvir
   * o evento 'resize' da window — ele não observa o próprio elemento container. Alternar a
   * classe .fullscreen muda a largura do container mas não dispara esse evento sozinho, então
   * sem isso o PDF continuaria renderizado no tamanho anterior.
   */
  private recalcularTamanhoDoPdf(): void {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));

      const altura = this.isFullscreen()
        ? Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)
        : this.alturaAntesDoFullscreen;

      if (altura === null) {
        return;
      }

      [200, 500, 900].forEach((atraso) => {
        setTimeout(() => this.avisarAlturaAoIframePai(altura), atraso);
      });
    });
  }

  /**
   * A tipagem oficial do @iframe-resizer/child declara `resize(customHeight?: string)`, mas o
   * código da própria lib valida o argumento com `typeof valor === 'number'` e lança TypeError
   * se receber uma string — por isso o cast abaixo para a assinatura real, em vez de seguir a
   * tipagem publicada (incorreta).
   */
  private avisarAlturaAoIframePai(altura: number): void {
    (window.parentIFrame as unknown as { resize?(customHeight?: number): void } | undefined)?.resize?.(altura);
  }
}
